import { useCallback, useEffect, useRef } from 'react';
import { analytics } from '../app/analytics';

type AuthMode = 'login' | 'signup';

export function useDescopeButtonListeners(mode: AuthMode) {
    const observerRef = useRef<MutationObserver | null>(null);

    const handleButtonClick = useCallback((e: Event, buttonType: 'social' | 'email') => {
        const btn = e.currentTarget as HTMLElement;
        let provider: string;

        if (buttonType === 'social') {
            provider = btn.getAttribute('data-descope-provider') || '';
        } else {
            provider = 'email';
        }

        if (provider) {
            const tracker = analytics.getTrackerInstance();
            if (tracker) {
                if (mode === 'signup') {
                    tracker.signupButtonClicked({ provider });
                } else {
                    tracker.loginButtonClicked({ provider });
                }
            } else {
                console.warn('Amplitude tracker not initialized, skipping analytics event');
            }
            console.log(`${buttonType} button clicked:`, provider, 'mode:', mode);
        }
    }, [mode]);

    const addButtonListeners = useCallback((selector: string, buttonType: 'social' | 'email') => {
        const descopeWc = document.querySelector('descope-wc');
        if (descopeWc && (descopeWc as any).shadowRoot) {
            const shadowRoot = (descopeWc as any).shadowRoot as ShadowRoot;
            const buttons = shadowRoot.querySelectorAll(selector);
            buttons.forEach(btn => {
                if ((btn as any)._clearittyHandler) {
                    btn.removeEventListener('click', (btn as any)._clearittyHandler);
                }
                const handler = (e: Event) => handleButtonClick(e, buttonType);
                (btn as any)._clearittyHandler = handler;
                btn.addEventListener('click', handler);
            });
        }
    }, [handleButtonClick]);

    const addAllButtonListeners = useCallback(() => {
        addButtonListeners('descope-button[data-type="social"]', 'social');
        addButtonListeners('vaadin-button[data-type="button"], descope-button[data-type="button"]', 'email');
    }, [addButtonListeners]);

    useEffect(() => {
        return () => {
            if (observerRef.current) observerRef.current.disconnect();
            const descopeWc = document.querySelector('descope-wc');
            if (descopeWc && (descopeWc as any).shadowRoot) {
                const shadowRoot = (descopeWc as any).shadowRoot as ShadowRoot;
                const allButtons = shadowRoot.querySelectorAll('descope-button[data-type="social"], vaadin-button[data-type="button"], descope-button[data-type="button"]');
                allButtons.forEach(btn => {
                    if ((btn as any)._clearittyHandler) {
                        btn.removeEventListener('click', (btn as any)._clearittyHandler);
                    }
                });
            }
        };
    }, []);

    useEffect(() => {
        if (observerRef.current) observerRef.current.disconnect();
        addAllButtonListeners();
        const descopeWc = document.querySelector('descope-wc');
        if (descopeWc && (descopeWc as any).shadowRoot) {
            const shadowRoot = (descopeWc as any).shadowRoot as ShadowRoot;
            observerRef.current = new MutationObserver(addAllButtonListeners);
            observerRef.current.observe(shadowRoot, { childList: true, subtree: true });
        }
        return () => {
            if (observerRef.current) observerRef.current.disconnect();
        };
    }, [mode, addAllButtonListeners]);

    return addAllButtonListeners;
}
